import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import classnames from '../../../_virtual/index.js';
import { useAccount, Selectors, State } from '@grandvisionhq/auth';
import { AddToCart } from '@grandvisionhq/carts-v2';
import { Card, Price, LineItemDeleteButton, MediaQuery } from '@grandvisionhq/common';
import { useLink, ProductType } from '@grandvisionhq/frk-routing';
import { Types } from '@grandvisionhq/graphql';
import { Icon } from '@grandvisionhq/icons';
import { StockIndicator } from '@grandvisionhq/inventory';
import { Spinner } from '@grandvisionhq/iris-ui';
import { BackInStockModal, BackInStockButton } from '@grandvisionhq/lib-visuals';
import { useIntl, useSelector, useFeatureToggles } from '@grandvisionhq/state';
import { useAddBackInStockSubscription, pushBackInStockSubscriptionEvent, pushBackInStockButtonClickEvent } from '@grandvisionhq/svc-back-in-stock';
import { placeholderImage } from '@grandvisionhq/utils';
import { pushWishlistEvent } from '../../../data-layer/wishlist.js';
import { getWishlistItemImage } from '../../../utils/get-wishlist-item-image.js';
import { WishlistItemDescription } from './WishlistItemDescription.js';

const WishlistItem = ({ isUpdateLoading , item , onUpdateWishList  })=>{
    const { getLabel  } = useIntl();
    const { useIsPhone  } = MediaQuery;
    const isPhone = useIsPhone();
    const authState = useAccount();
    const { email: userEmail  } = useSelector(Selectors.getAuth);
    const isUserLoggedIn = authState === State.AuthState.LoggedIn;
    const { isBackInStockEnabled , isPrescriptionGlassesEnabled  } = useFeatureToggles();
    const { addBackInStockSubscription , isSubscribed  } = useAddBackInStockSubscription();
    const [isBackInStockModalOpen, setIsBackInStockModalOpen] = React.useState(false);
    const { name , sku , brand , originalPrice , discountedPrice , discountValue , currencyCode , slug , productType , showBackInStockNotification , isAllowBackOrder , stockCount , channelAvailability , isExcludedFromCheckout ,  } = item;
    const handleNotifyClick = ()=>{
        pushBackInStockButtonClickEvent(productType, name, brand);
        setIsBackInStockModalOpen(true);
    };
    const handleModalClose = ()=>setIsBackInStockModalOpen(false)
    ;
    const handleBackInStockFormSubmit = function() {
        var _ref = swcHelpers.asyncToGenerator(function*(email) {
            const success = yield addBackInStockSubscription({
                email,
                sku
            });
            const backInStockSubscriptionEvent = {
                sessionStatus: isUserLoggedIn ? 'logged_in' : 'logged_out',
                itemType: productType,
                itemName: name,
                itemBrand: brand,
                message_type: success ? 'success' : 'error'
            };
            pushBackInStockSubscriptionEvent(backInStockSubscriptionEvent);
        });
        return function handleBackInStockFormSubmit(email) {
            return _ref.apply(this, arguments);
        };
    }();
    const labels = {
        removeItem: getLabel('account.wishlist.item.remove'),
        discount: getLabel('cart.item.discount'),
        notify: getLabel('account.wishlist.item.notify'),
        notifyAriaLabel: getLabel('account.wishlist.item.notify.ariaLabel'),
        notificationSet: getLabel('account.wishlist.item.notificationSet')
    };
    const productPageHref = useLink({
        route: 'products.detail',
        params: {
            slug,
            sku,
            key: productType
        }
    }, {
        withParams: false
    });
    const onWishlistItemClick = ()=>{
        pushWishlistEvent({
            siteFeature: 'my_account',
            interactionType: 'wishlist_product_click',
            userLogged: isUserLoggedIn ? 'logged_in' : 'logged_out',
            item: {
                sku
            }
        });
    };
    const isRXItem = item.productType === ProductType.Frames && item.isPrescriptionNeeded;
    // The button should only show up if
    // the item is not excluded from checkout
    // and not a contact lenses item
    // and not a ready readers item
    // and not a rx item
    // and the item is available online
    // and the item is in stock
    const showAddToCartButton = Boolean(!isExcludedFromCheckout && productType !== ProductType.ContactLenses && productType !== ProductType.ReadyReaders && !isRXItem && channelAvailability === Types.ChannelAvailability.ONLINE && stockCount && stockCount > 0);
    const showStockIndicator = (productType !== ProductType.Frames || isPrescriptionGlassesEnabled) && !isExcludedFromCheckout;
    const itemDraft = {
        sku,
        quantity: 1,
        type: 'regular'
    };
    var ref;
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(BackInStockModal, {
        isOpen: isBackInStockModalOpen,
        onModalClose: handleModalClose,
        onSubmit: handleBackInStockFormSubmit,
        userEmail: userEmail
    }), /*#__PURE__*/ React.createElement(Card, {
        layout: isPhone ? 'vertical' : 'horizontal',
        className: "wishlist-item"
    }, /*#__PURE__*/ React.createElement(Card.Image, {
        backgroundColor: "var(--color--shade-lightest)",
        alt: name,
        src: (ref = getWishlistItemImage(item)) !== null && ref !== void 0 ? ref : placeholderImage.url,
        imageHref: productPageHref === null || productPageHref === void 0 ? void 0 : productPageHref.href,
        className: "wishlist-item__image",
        onClick: onWishlistItemClick
    }), /*#__PURE__*/ React.createElement(Card.Content, null, /*#__PURE__*/ React.createElement("div", {
        className: "wishlist-item__product-content"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "wishlist-item__main-column"
    }, /*#__PURE__*/ React.createElement(Card.Section, {
        className: "wishlist-item__top-row"
    }, /*#__PURE__*/ React.createElement(Card.Heading, {
        component: "h2",
        className: "wishlist-item__title",
        variant: "subheading1"
    }, name)), /*#__PURE__*/ React.createElement(Card.Section, {
        className: "wishlist-item__attributes"
    }, /*#__PURE__*/ React.createElement(WishlistItemDescription, {
        item: item
    }))), /*#__PURE__*/ React.createElement("div", {
        className: "wishlist-item__side-column"
    }, /*#__PURE__*/ React.createElement(Card.Section, {
        className: "wishlist-item__prices-container"
    }, discountedPrice !== 0 && discountedPrice ? /*#__PURE__*/ React.createElement(Price, {
        value: discountedPrice,
        currency: currencyCode,
        centAmount: false,
        className: classnames('wishlist-item__price', 'wishlist-item__price--large')
    }) : null, originalPrice !== 0 && originalPrice ? /*#__PURE__*/ React.createElement(Price, {
        value: originalPrice,
        currency: currencyCode,
        type: discountedPrice && discountedPrice > 0 ? 'strikethrough' : 'default',
        centAmount: false,
        className: classnames('wishlist-item__price', {
            'wishlist-item__price--large': !discountedPrice
        })
    }) : null, discountValue !== 0 && discountValue ? /*#__PURE__*/ React.createElement(Card.Text, {
        className: "wishlist-item__discount-information"
    }, /*#__PURE__*/ React.createElement("span", {
        className: "wishlist-item__discount-label"
    }, labels.discount, "\xa0"), /*#__PURE__*/ React.createElement("span", null, discountValue, "%")) : null))), /*#__PURE__*/ React.createElement("div", {
        className: "wishlist-item__cta"
    }, showStockIndicator ? /*#__PURE__*/ React.createElement(Card.Section, null, /*#__PURE__*/ React.createElement(StockIndicator, {
        allowBackOrder: !!isAllowBackOrder,
        stockCount: stockCount !== null && stockCount !== void 0 ? stockCount : undefined,
        channelAvailability: channelAvailability
    })) : null, /*#__PURE__*/ React.createElement(Card.Section, {
        flex: true,
        flexContent: true,
        className: "wishlist-item__actions"
    }, isUpdateLoading ? /*#__PURE__*/ React.createElement(Spinner, {
        className: "wishlist-item__loading",
        "aria-label": getLabel('common.loadingText')
    }) : /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: "wishlist-item__left-actions"
    }, showBackInStockNotification && isBackInStockEnabled ? isSubscribed ? /*#__PURE__*/ React.createElement("div", {
        className: "wishlist-item__notification"
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "checkmark"
    }), /*#__PURE__*/ React.createElement(Card.Text, {
        variant: "body-head"
    }, labels.notificationSet)) : /*#__PURE__*/ React.createElement(BackInStockButton, {
        onNotifyClick: handleNotifyClick
    }) : null, showAddToCartButton && /*#__PURE__*/ React.createElement(AddToCart, {
        variant: "ghost",
        className: "wishlist-item__add-to-cart",
        icon: "add-to-cart",
        caption: getLabel('cart.actions.addToCart'),
        lineItemDrafts: [
            itemDraft
        ]
    })), /*#__PURE__*/ React.createElement("div", {
        className: "wishlist-item__right-actions"
    }, /*#__PURE__*/ React.createElement(LineItemDeleteButton, {
        onClick: onUpdateWishList,
        "aria-label": labels.removeItem
    }))))))));
};

export { WishlistItem as default };
//# sourceMappingURL=WishlistItem.js.map
